import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Spinner from '../../../util/spinner-shim'
import { injectIntl } from 'react-intl'
import { fromC } from 'altrac-conversions'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import ChartWindMachine, { makeRows, makeColumns } from './ChartWindMachine'
import ChartDataLoading from './ChartDataLoading'
import { useSearchParams } from 'react-router-dom'
import AppStore from '../../../stores/AppStore.js'

const Cell = function Cell ({ value }) {
  const returnVal = <td>{value}</td>
  return returnVal
}

Cell.propTypes = {
  value: PropTypes.node,
}

const Row = function Row ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i += 1) {
    if (i !== 1) {
      let cellValue
      if (data[i] !== null && typeof data[i].getMonth === 'function') {
        cellValue = data[i].toLocaleString()
      } else {
        cellValue = data[i]
      }
      cells.push(<Cell key={i} value={cellValue} />)
    }
  }
  return <tr>{cells}</tr>
}

Row.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({
  sensorData, device, dataType, formatMessage,
}) {
  const user = AppStore.getUser()
  const {
    locale: { tempConv: tempPreference },
  } = user

  if (sensorData.length < 1) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }
  const applicationSettings = device.application_settings || { settings: {} }
  const tempStart = fromC(applicationSettings.settings.tempStart, tempPreference, 1)
  const rows = makeRows(sensorData, device, dataType, tempPreference, tempStart, {
    temperature: formatMessage({
      id: 'sensor.temperature.ambient.short',
      defaultMessage: 'Temperature',
      description: 'Temperature',
    }),
    temperatureStart: formatMessage({
      id: 'setting.tempStart',
      defaultMessage: 'Start Temperature',
      description: 'Start Temperature',
    }),
    speed: formatMessage({
      id: 'sensor.speed',
      defaultMessage: 'Speed',
      description: 'Speed',
    }),
  })
  const rowsArr = []
  for (let i = 0; i < rows.length; i += 1) {
    rowsArr.push(<Row key={i} data={rows[i]} />)
  }
  const columns = makeColumns([
    {
      type: 'date',
      label: formatMessage({
        id: 'sensor.time.short',
        defaultMessage: 'Time',
        description: 'Time',
      }),
      role: 'domain',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.speed',
        defaultMessage: 'Speed',
        description: 'Speed',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.temperature.ambient.short',
        defaultMessage: 'Temperature',
        description: 'Temperature',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'setting.tempStart',
        defaultMessage: 'Start Temperature',
        description: 'Start Temperature',
      }),
      role: 'data',
    },
  ])
  const columnArr = []
  for (let i = 0; i < columns.length; i += 1) {
    columnArr.push(columns[i].label)
  }
  return (
    <div className='telemetryTableWrapper'>
      <table className='table table-striped'>
        <thead>
          <Row data={columnArr} />
        </thead>
        <tbody>{rowsArr}</tbody>
      </table>
    </div>
  )
}

Table.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  device: PropTypes.object,
  dataType: PropTypes.string,
  formatMessage: PropTypes.func,
}

const TelemetryWindMachine = function TelemetryWindMachine (props) {
  const {
    device,
    periodStart,
    periodStop,
    sensorData,
    metricReady,
    intl: { formatMessage },
  } = props
  const [searchParams] = useSearchParams()
  const dataType = searchParams.get('dataType')

  const intervalRef = useRef(null)

  const getNewData = (address, periodStartParam, periodStopParam) => {
    AppDispatcher.dispatch({
      action: 'get-metric-address',
      address: address || device.address,
      timeBeg: periodStartParam || periodStart,
      timeEnd: periodStopParam || periodStop,
    })
  }

  useEffect(() => {
    getNewData()
    intervalRef.current = setInterval(getNewData, 60000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    getNewData(null, periodStart, periodStop)
  }, [periodStart, periodStop])

  if (!Array.isArray(sensorData) || sensorData.length < 1 || !metricReady) {
    return <ChartDataLoading />
  }
  let dataTypeTemp = '134'
  if (dataType) {
    dataTypeTemp = dataType
  }
  return (
    <div className='deviceInfo'>
      <ChartWindMachine
        sensorData={sensorData}
        metricReady={metricReady}
        device={device}
        dataType={dataTypeTemp}
      />
      <Table
        sensorData={sensorData}
        device={device}
        dataType={dataTypeTemp}
        formatMessage={formatMessage}
      />
    </div>
  )
}

TelemetryWindMachine.propTypes = {
  device: PropTypes.object,
  location: PropTypes.object,
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  sensorData: PropTypes.array,
  metricReady: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(TelemetryWindMachine)
